import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonService } from '../../services/common.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { QueueService } from '../../services/queue.service';
import * as CommonActions from './actions';
import { ConnectionsService } from '../../services/connections.service';
import { MessageTemplatesService } from '../../services/message-templates.service';
import { QuickAnswerService } from '../../services/quick-answer.service';
import { EndpointsMaintenanceService } from '../../services/endpoints-maintenance.service';
import { EmailsMaintenanceService } from '../../services/emails-maintenance.service';
import { OptionsMaintenanceService } from '../../services/options-maintenance.service';
import { IntegrationsService } from '../../services/integrations.service';

@Injectable()
export class CommonEffects {
  httpError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommonActions.httpError),
        tap((action) => {
          if (action.error.status == 401) {
            this.snackbar.open(
              'No tienes permisos para realizar esta operación'
            );
            return;
          }

          this.snackbar.open('Ocurrió un error al procesar la solicitud');
        })
      ),
    { dispatch: false }
  );

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getRoles),
      exhaustMap((action) =>
        this.commonService.getRoles().pipe(
          map((roles) => CommonActions.getRolesSuccess({ roles })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  checkAuthStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.checkAuthStatus),
      exhaustMap((action) =>
        this.authService.isAuthenticated().pipe(
          map((response) => CommonActions.checkAuthStatusSuccess(response)),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.logout),
      exhaustMap((action) =>
        this.authService.logout().pipe(
          map((response) => CommonActions.logoutSuccess()),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CommonActions.logoutSuccess),
        tap((action) => {
          this.cookieService.delete('connect.sid');
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  getQueues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getQueues),
      exhaustMap((action) =>
        this.queueService.getQueues().pipe(
          map((queues) => CommonActions.getQueuesSuccess({ queues })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getTicketStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTicketStatuses),
      exhaustMap((action) =>
        this.commonService.getTicketStatuses().pipe(
          map((ticketStatuses) =>
            CommonActions.getTicketStatusesSuccess({ ticketStatuses })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getConnections),
      exhaustMap((action) =>
        this.connectionsService.getConnections().pipe(
          map((connections) =>
            CommonActions.getConnectionsSuccess({ connections })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTemplates),
      exhaustMap((action) =>
        this.messageTemplatesService.getTemplates(action.wabaId).pipe(
          map((templates) => CommonActions.getTemplatesSuccess({ templates })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getTemplatesByQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTemplatesByQueue),
      exhaustMap((action) =>
        this.messageTemplatesService.getTemplatesByQueue(action.queueId).pipe(
          map((templates) =>
            CommonActions.getTemplatesByQueueSuccess({ templates })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getQAns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getQuickAnswers),
      exhaustMap((action) =>
        this.quickAnswerService.getAllQAns().pipe(
          map((quick_answers) =>
            CommonActions.getQuickAnswersSuccess({ quick_answers })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getEndpoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getEndpoints),
      exhaustMap((action) =>
        this.endpointService.getEndpoints().pipe(
          map((endpoints) => CommonActions.getEndpointsSuccess({ endpoints })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getEmailOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getMailableOptions),
      exhaustMap((action) =>
        this.emailOptionsService.getEmailOptions().pipe(
          map((emails) => CommonActions.getMailableOptionsSuccess({ emails })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getOptions),
      exhaustMap((action) =>
        this.optionsMaintenanceService.getOptions().pipe(
          map((options) => CommonActions.getOptionsSuccess({ options })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getTenant),
      exhaustMap((action) =>
        this.authService.getSessionTenant().pipe(
          map((tenant) => CommonActions.getTenantSuccess({ tenant })),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getIntegrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getIntegrations),
      exhaustMap(() =>
        this.integrationsService.getIntegrations().pipe(
          map((integrations) =>
            CommonActions.getIntegrationsSuccess({ integrations })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getChatbotStepTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getChatbotStepTypes),
      exhaustMap((action) =>
        this.commonService.getChatbotStepTypes().pipe(
          map((stepTypes) =>
            CommonActions.getChatbotStepTypesSuccess({ stepTypes })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getChatbotIntegrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getChatbotIntegrations),
      exhaustMap((action) =>
        this.commonService.getChatbotIntegrations().pipe(
          map((chatbotIntegrations) =>
            CommonActions.getChatbotIntegrationsSuccess({ chatbotIntegrations })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getNlpProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getNlpProviders),
      exhaustMap((action) =>
        this.commonService.getNlpProviders().pipe(
          map((nlpProviders) =>
            CommonActions.getNlpProvidersSuccess({ nlpProviders })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  getNlpEntities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonActions.getNlpEntities),
      exhaustMap((action) =>
        this.commonService.getNlpEntities().pipe(
          map((nlpEntities) =>
            CommonActions.getNlpEntitiesSuccess({ nlpEntities })
          ),
          catchError((error) => of(CommonActions.httpError({ error })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private snackbar: MatSnackBar,
    private commonService: CommonService,
    private authService: AuthService,
    private router: Router,
    private cookieService: SsrCookieService,
    private queueService: QueueService,
    private connectionsService: ConnectionsService,
    private messageTemplatesService: MessageTemplatesService,
    private quickAnswerService: QuickAnswerService,
    private endpointService: EndpointsMaintenanceService,
    private emailOptionsService: EmailsMaintenanceService,
    private optionsMaintenanceService: OptionsMaintenanceService,
    private integrationsService: IntegrationsService
  ) {}
}
